import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Toaster } from 'sonner';
import { Star, ArrowLeft, CircleNotch, Info } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import BlindSpotsBanner from '../components/BlindSpotsBanner';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const confColor = (c) => ({
  high: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  medium: 'bg-amber-100 text-amber-800 border-amber-300',
  low: 'bg-orange-100 text-orange-800 border-orange-300',
  very_low: 'bg-rose-100 text-rose-800 border-rose-300',
}[c] || 'bg-gray-100 text-gray-700');

const bfColor = (bf) => {
  if (bf >= 2) return 'bg-emerald-100 text-emerald-800 ring-2 ring-emerald-400';
  if (bf >= 1.3) return 'bg-emerald-50 text-emerald-700';
  if (bf >= 0.8) return 'bg-gray-50 text-gray-600';
  if (bf >= 0.4) return 'bg-amber-50 text-amber-700';
  return 'bg-rose-50 text-rose-700';
};

const bfLabel = (bf) => {
  if (bf >= 2) return 'Strong +';
  if (bf >= 1.3) return 'Mild +';
  if (bf >= 0.8) return 'Neutral';
  if (bf >= 0.4) return 'Mild -';
  return 'Strong -';
};

export default function Window22Bayesian() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [allData, setAllData] = useState(null);
  const [simData, setSimData] = useState(null);   // /api/analysis/draw-similarity
  const [loading, setLoading] = useState(true);
  const [tolerance, setTolerance] = useState('medium');

  const fetchData = useCallback(async (tol) => {
    setLoading(true);
    try {
      const [single, all, sim] = await Promise.all([
        axios.get(`${API}/analysis/bayesian-posterior`, { params: { tolerance: tol } }),
        axios.get(`${API}/analysis/bayesian-posterior-all`),
        axios.get(`${API}/analysis/draw-similarity`),
      ]);
      setData(single.data);
      setAllData(all.data);
      setSimData(sim.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(tolerance); }, [tolerance, fetchData]);

  useEffect(() => {
    const h = () => fetchData(tolerance);
    window.addEventListener('euromillions:draws-updated', h);
    return () => window.removeEventListener('euromillions:draws-updated', h);
  }, [tolerance, fetchData]);

  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
  const ballLabels = { ball_1: 'B1', ball_2: 'B2', ball_3: 'B3', ball_4: 'B4', ball_5: 'B5' };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" />
      <header className="bg-white border-b">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
            <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Window 22 — Draw Similarity, Pattern Succession, Feature Similarity & Bayesian Posterior</h1>
              <p className="text-xs text-gray-500">Five W22-derived predictors — the same signals surfaced in W41 (Summary Dashboard) and W50 (Accuracy Tracker)</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 font-semibold">Tolerance:</label>
            {['tight', 'medium', 'loose'].map((t) => (
              <button
                key={t}
                onClick={() => setTolerance(t)}
                className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${tolerance === t ? 'bg-[#002FA7] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                data-testid={`tol-${t}`}
              >
                {t.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">


        <WindowNarrative windowId="W22" {...NARRATIVES.W22} className="mb-5" />
        {loading ? (
          <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>
        ) : data?.error ? (
          <div className="bg-rose-50 border border-rose-200 rounded p-6 text-center text-rose-700 font-bold">{data.error}</div>
        ) : data ? (
          <>
            {data?.risk_flags && Object.keys(data.risk_flags).length > 0 && (
              <div className="mb-6">
                <BlindSpotsBanner
                  riskFlags={data.risk_flags}
                  topPicks={Object.keys(data.risk_flags).map(k => ({ id: k, tier: k === '__window__' ? 'WINDOW' : 'TOP' }))}
                  itemLabel="numbers"
                  renderItem={(id) => {
                    if (id === '__window__') return <span className="font-bold">Window-Wide</span>;
                    const [bk, num] = String(id).split('|');
                    const ballNum = bk.replace('ball_', '');
                    return <span className="font-mono">B{ballNum}: {num}</span>;
                  }}
                />
              </div>
            )}
            {/* Block 1 — Context */}
            <section className="bg-white border rounded-lg p-5 mb-6" data-testid="block-context">
              <h2 className="font-bold text-gray-900 mb-3">Block 1 — Last Draw Feature Profile & Precedent Sample</h2>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-3">
                <div className="bg-slate-50 rounded p-3"><p className="text-[10px] uppercase text-gray-500 font-bold">Last Draw</p><p className="font-mono font-bold text-lg">#{data.last_draw.draw}</p></div>
                <div className="bg-slate-50 rounded p-3"><p className="text-[10px] uppercase text-gray-500 font-bold">Balls</p><p className="font-mono font-bold">{(data.last_draw.balls || []).join(', ')}</p></div>
                <div className="bg-slate-50 rounded p-3"><p className="text-[10px] uppercase text-gray-500 font-bold">Sum / Spread</p><p className="font-mono font-bold">{data.last_draw.sum} / {data.last_draw.spread}</p></div>
                <div className="bg-slate-50 rounded p-3"><p className="text-[10px] uppercase text-gray-500 font-bold">Odds / High</p><p className="font-mono font-bold">{data.last_draw.odds}O / {data.last_draw.high}H</p></div>
                <div className="bg-slate-50 rounded p-3"><p className="text-[10px] uppercase text-gray-500 font-bold">Avg Gap</p><p className="font-mono font-bold">{data.last_draw.avg_gap}</p></div>
                <div className={`rounded p-3 border ${confColor(data.confidence)}`}>
                  <p className="text-[10px] uppercase font-bold">Confidence</p>
                  <p className="font-mono font-bold">{data.confidence.toUpperCase().replace('_', ' ')}</p>
                  <p className="text-[9px] mt-0.5">n={data.n_precedents} precedents</p>
                </div>
              </div>
              <p className="text-xs text-gray-600 flex items-start gap-1.5">
                <Info size={14} className="mt-0.5 flex-shrink-0" />
                <span>{data.confidence_note} Tolerance <b>{data.tolerance.toUpperCase()}</b>: Sum±{data.bands?.sum} · Spread±{data.bands?.spread} · AvgGap±{data.bands?.avg_gap} · Odds {data.bands?.odds_exact ? 'exact' : '±1'} · High {data.bands?.high_exact ? 'exact' : '±1'}</span>
              </p>
            </section>

            {/* Block 2A — W22 Draw Similarity (top-5 successor numbers per ball) */}
            {simData && !simData.error && (
              <section className="bg-white border rounded-lg p-5 mb-6" data-testid="block-draw-similarity">
                <h2 className="font-bold text-gray-900 mb-1">Block 2A — W22 Draw Similarity · Top-5 Successors Per Ball</h2>
                <p className="text-xs text-gray-500 mb-4">
                  Nearest-neighbour lookup: finds the {(simData.top_similar || []).length} historical draws most similar to the last draw and counts what came at each ball position in the NEXT draw. Top-5 per ball (same as W41 & W50).
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="draw-sim-table">
                    <thead className="bg-slate-800 text-white">
                      <tr>
                        <th className="px-3 py-2 text-left font-bold">Ball</th>
                        <th className="px-3 py-2 text-center font-bold">#1</th>
                        <th className="px-3 py-2 text-center font-bold">#2</th>
                        <th className="px-3 py-2 text-center font-bold">#3</th>
                        <th className="px-3 py-2 text-center font-bold">#4</th>
                        <th className="px-3 py-2 text-center font-bold">#5</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ballKeys.map((bk) => {
                        const preds = (simData.predictions?.[bk] || []).slice(0, 5);
                        return (
                          <tr key={bk} className="border-b hover:bg-gray-50" data-testid={`draw-sim-row-${bk}`}>
                            <td className="px-3 py-2 font-bold text-slate-700">{ballLabels[bk]}</td>
                            {[0, 1, 2, 3, 4].map((i) => {
                              const p = preds[i];
                              if (!p) return <td key={i} className="px-3 py-2 text-center text-gray-300">–</td>;
                              return (
                                <td key={i} className="px-3 py-2 text-center">
                                  <div className="inline-flex flex-col items-center bg-blue-50 border border-blue-200 rounded px-2 py-1" data-testid={`draw-sim-${bk}-rank-${i + 1}`}>
                                    <span className="font-mono font-black text-lg leading-none text-blue-900">{p.number}</span>
                                    <span className="text-[9px] font-mono leading-tight mt-0.5 text-blue-700">n={p.count}</span>
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-gray-500 mt-3 italic">
                  n = number of similar-precedent successors that landed the same number at this ball position.
                </p>
              </section>
            )}

            {/* Block 2B — W22 Pattern Succession (top-5 numbers per ball) */}
            {simData && !simData.error && (
              <section className="bg-white border rounded-lg p-5 mb-6" data-testid="block-pattern-succession">
                <h2 className="font-bold text-gray-900 mb-1">Block 2B — W22 Pattern Succession · Top-5 Per Ball</h2>
                <p className="text-xs text-gray-500 mb-4">
                  Range-bucket pattern match: last draw&apos;s pattern is <span className="font-mono font-bold text-emerald-700">{simData.last_pattern || '—'}</span> ({(simData.last_pattern_labels || []).join(' · ')}). {simData.pattern_match_count} historical draws share this pattern; top-5 numbers per ball from their successors.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="pat-succ-table">
                    <thead className="bg-slate-800 text-white">
                      <tr>
                        <th className="px-3 py-2 text-left font-bold">Ball</th>
                        <th className="px-3 py-2 text-center font-bold">#1</th>
                        <th className="px-3 py-2 text-center font-bold">#2</th>
                        <th className="px-3 py-2 text-center font-bold">#3</th>
                        <th className="px-3 py-2 text-center font-bold">#4</th>
                        <th className="px-3 py-2 text-center font-bold">#5</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ballKeys.map((bk) => {
                        const preds = (simData.pattern_predictions?.[bk] || []).slice(0, 5);
                        return (
                          <tr key={bk} className="border-b hover:bg-gray-50" data-testid={`pat-succ-row-${bk}`}>
                            <td className="px-3 py-2 font-bold text-slate-700">{ballLabels[bk]}</td>
                            {[0, 1, 2, 3, 4].map((i) => {
                              const p = preds[i];
                              if (!p) return <td key={i} className="px-3 py-2 text-center text-gray-300">–</td>;
                              return (
                                <td key={i} className="px-3 py-2 text-center">
                                  <div className="inline-flex flex-col items-center bg-emerald-50 border border-emerald-200 rounded px-2 py-1" data-testid={`pat-succ-${bk}-rank-${i + 1}`}>
                                    <span className="font-mono font-black text-lg leading-none text-emerald-900">{p.number}</span>
                                    <span className="text-[9px] font-mono leading-tight mt-0.5 text-emerald-700">n={p.count}</span>
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Block 2C — W22 Feature Similarity (top-5 validated 5-ball combos) */}
            {simData && !simData.error && (simData.feature_combos || []).length > 0 && (
              <section className="bg-white border rounded-lg p-5 mb-6" data-testid="block-feature-similarity">
                <h2 className="font-bold text-gray-900 mb-1">Block 2C — W22 Feature Similarity · Top-5 Validated 5-Ball Combos</h2>
                <p className="text-xs text-gray-500 mb-4">
                  Combinatorial gen from Draw-Similarity per-ball candidates, filtered by validation rules (sum, spread, odds, high) + W15 balance boost. Top-5 by score.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="feat-sim-table">
                    <thead className="bg-slate-800 text-white">
                      <tr>
                        <th className="px-3 py-2 text-center font-bold">Rank</th>
                        <th className="px-3 py-2 text-center font-bold">Balls (sorted)</th>
                        <th className="px-3 py-2 text-center font-bold">Sum</th>
                        <th className="px-3 py-2 text-center font-bold">Spread</th>
                        <th className="px-3 py-2 text-center font-bold">Odds/High</th>
                        <th className="px-3 py-2 text-center font-bold">Pattern</th>
                        <th className="px-3 py-2 text-center font-bold">Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(simData.feature_combos || []).slice(0, 5).map((c, i) => (
                        <tr key={i} className={i === 0 ? 'bg-amber-50 ring-2 ring-amber-300' : 'border-b hover:bg-gray-50'} data-testid={`feat-sim-row-${i + 1}`}>
                          <td className="px-3 py-2 text-center font-mono font-black text-slate-700">#{i + 1}</td>
                          <td className="px-3 py-2 text-center">
                            <div className="inline-flex gap-1">
                              {(c.balls || []).map((n, j) => (
                                <span key={j} className="inline-block bg-purple-100 border border-purple-300 rounded px-2 py-0.5 font-mono font-bold text-purple-900">{n}</span>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center font-mono font-bold">{c.sum}</td>
                          <td className="px-3 py-2 text-center font-mono">{c.spread}</td>
                          <td className="px-3 py-2 text-center font-mono">
                            <span className={c.oe_match ? 'text-emerald-700 font-bold' : 'text-gray-500'}>{c.odds}O</span>
                            <span className="mx-1 text-gray-400">/</span>
                            <span className={c.hl_match ? 'text-emerald-700 font-bold' : 'text-gray-500'}>{c.high}H</span>
                          </td>
                          <td className="px-3 py-2 text-center font-mono text-slate-600">{c.pattern}</td>
                          <td className="px-3 py-2 text-center font-mono font-black text-emerald-700">{c.score}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-gray-500 mt-3 italic">
                  Bold O/H = matches W15 balance target. w15_boost applied where applicable.
                </p>
              </section>
            )}


            {/* Block 3 — Bayesian All Tolerances Comparison (TIGHT / MEDIUM / LOOSE — top-5 per ball) */}
            {allData && !allData.error && (
              <section className="bg-white border rounded-lg p-5 mb-6" data-testid="block-all-tolerances">
                <h2 className="font-bold text-gray-900 mb-1">Block 3 — W22 Bayesian Posterior · Top-5 Per Ball Across All 3 Tolerances</h2>
                <p className="text-xs text-gray-500 mb-4">Side-by-side comparison: TIGHT (strict match, fewer precedents) → MEDIUM → LOOSE (broader match, more precedents). Top-5 numbers per ball per tolerance — same feed W41 consumes.</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="all-tol-table">
                    <thead className="bg-slate-800 text-white">
                      <tr>
                        <th className="px-2 py-2 text-left font-bold">Tolerance</th>
                        <th className="px-2 py-2 text-center font-bold">n</th>
                        <th className="px-2 py-2 text-center font-bold">Conf.</th>
                        {ballKeys.map(bk => (
                          <th key={bk} className="px-2 py-2 text-center font-bold" colSpan={5}>{ballLabels[bk]} (top-5)</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {['tight', 'medium', 'loose'].map((tol) => {
                        const block = allData[tol];
                        if (!block || block.error) return null;
                        return (
                          <tr key={tol} className={tol === tolerance ? 'bg-indigo-50 ring-2 ring-indigo-300' : 'border-b hover:bg-gray-50'} data-testid={`all-tol-row-${tol}`}>
                            <td className="px-2 py-2">
                              <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase ${tol === 'tight' ? 'bg-blue-200 text-blue-900' : tol === 'medium' ? 'bg-amber-200 text-amber-900' : 'bg-rose-200 text-rose-900'}`}>{tol}</span>
                            </td>
                            <td className="px-2 py-2 text-center font-mono font-bold">{block.n_precedents}</td>
                            <td className="px-2 py-2 text-center">
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${confColor(block.confidence)}`}>{block.confidence.toUpperCase().replace('_', ' ')}</span>
                            </td>
                            {ballKeys.map((bk) => {
                              const ranked = ((block.per_ball_ranked || {})[bk] || []).slice(0, 5);
                              return (
                                <React.Fragment key={bk}>
                                  {[0, 1, 2, 3, 4].map((i) => {
                                    const pick = ranked[i];
                                    if (!pick) {
                                      return <td key={i} className="px-1 py-1.5 text-center text-gray-300">–</td>;
                                    }
                                    return (
                                      <td key={i} className="px-1 py-1.5 text-center" data-testid={`bayes-${tol}-${bk}-rank-${i + 1}`}>
                                        <div className={`inline-flex flex-col items-center rounded px-1.5 py-0.5 ${bfColor(pick.bayes_factor)}`}>
                                          <span className="font-mono font-black text-sm leading-none">{pick.number}</span>
                                          <span className="text-[8px] font-mono leading-tight mt-0.5">BF {pick.bayes_factor}×</span>
                                          <span className="text-[8px] opacity-70 leading-tight">n={pick.sample_count}</span>
                                        </div>
                                      </td>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-gray-500 mt-3 italic">
                  Read: numbers where all 3 tolerances agree (same rank or repeat pick) are strongest signals. If TIGHT and LOOSE diverge sharply, the MEDIUM tolerance is usually the best compromise.
                </p>
              </section>
            )}

            {/* Block 4 — Top picks per ball (for active tolerance) */}
            <section className="bg-white border rounded-lg p-5 mb-6" data-testid="block-top-picks">
              <h2 className="font-bold text-gray-900 mb-1">Block 4 — Top Bayesian Pick Per Ball ({data.tolerance.toUpperCase()})</h2>
              <p className="text-xs text-gray-500 mb-4">Highest Bayes Factor (posterior / prior) with sample support ≥ 2 precedents</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {ballKeys.map((bk) => {
                  const pick = data.top_picks?.[bk];
                  if (!pick) return null;
                  return (
                    <div key={bk} className={`rounded-lg p-4 border-2 ${bfColor(pick.bayes_factor)}`} data-testid={`top-${bk}`}>
                      <div className="text-[10px] font-bold uppercase opacity-70">{ballLabels[bk]}</div>
                      <div className="flex items-end gap-2 my-1">
                        <div className="text-4xl font-mono font-black">{pick.number}</div>
                        <div className="text-[10px] pb-1">
                          <div>Prior {pick.prior_pct}%</div>
                          <div className="font-bold">Post {pick.posterior_pct}%</div>
                        </div>
                      </div>
                      <div className="text-[11px] font-mono font-bold">BF {pick.bayes_factor}× <span className="text-[9px] opacity-70">({bfLabel(pick.bayes_factor)})</span></div>
                      <div className="text-[9px] opacity-60">n={pick.sample_count} precedents</div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Block 5 — Full posterior table per ball */}
            <section className="bg-white border rounded-lg p-5 mb-6" data-testid="block-posterior-tables">
              <h2 className="font-bold text-gray-900 mb-1">Block 5 — Per-Ball Posterior Distribution (Top 15 by Bayes Factor)</h2>
              <p className="text-xs text-gray-500 mb-4">BF &gt; 1 = over-indexed given context · BF &lt; 1 = under-indexed</p>
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                {ballKeys.map((bk) => {
                  const rows = data.per_ball_posteriors?.[bk] || [];
                  return (
                    <div key={bk} className="border rounded overflow-hidden">
                      <div className="bg-slate-800 text-white px-3 py-2 font-bold">{ballLabels[bk]}</div>
                      <table className="w-full text-[11px]">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-1.5 py-1.5 text-left">#</th>
                            <th className="px-1.5 py-1.5 text-right">Prior</th>
                            <th className="px-1.5 py-1.5 text-right">Post</th>
                            <th className="px-1.5 py-1.5 text-right">n</th>
                            <th className="px-1.5 py-1.5 text-right">BF</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r, i) => (
                            <tr key={r.number} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-1.5 py-1.5 font-mono font-bold">{r.number}</td>
                              <td className="px-1.5 py-1.5 font-mono text-right text-gray-500">{r.prior_pct}%</td>
                              <td className="px-1.5 py-1.5 font-mono text-right font-bold">{r.posterior_pct}%</td>
                              <td className="px-1.5 py-1.5 font-mono text-right text-gray-500">{r.sample_count}</td>
                              <td className={`px-1.5 py-1.5 font-mono text-right font-bold ${r.bayes_factor >= 1.3 ? 'text-emerald-700' : r.bayes_factor <= 0.7 ? 'text-rose-700' : 'text-gray-600'}`}>{r.bayes_factor}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Block 6 — Recent precedents evidence */}
            <section className="bg-white border rounded-lg p-5 mb-6" data-testid="block-precedents">
              <h2 className="font-bold text-gray-900 mb-1">Block 6 — Recent Historical Precedents (up to 20)</h2>
              <p className="text-xs text-gray-500 mb-4">Draws whose feature profile matched the last draw — and what came next. This is the evidence base behind the posterior.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="precedents-table">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-2 py-2 text-left">Precedent Draw</th>
                      <th className="px-2 py-2 text-left">Date</th>
                      <th className="px-2 py-2 text-left">Balls</th>
                      <th className="px-2 py-2 text-center">Sum</th>
                      <th className="px-2 py-2 text-center">Spread</th>
                      <th className="px-2 py-2 text-left bg-blue-50">Successor Draw</th>
                      <th className="px-2 py-2 text-left bg-blue-50">Successor Balls</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.recent_precedents || []).map((p, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-2 py-1.5 font-mono font-bold">#{p.prec_draw}</td>
                        <td className="px-2 py-1.5 text-gray-600">{p.prec_date}</td>
                        <td className="px-2 py-1.5 font-mono">{(p.prec_balls || []).join(', ')}</td>
                        <td className="px-2 py-1.5 font-mono text-center">{p.prec_sum}</td>
                        <td className="px-2 py-1.5 font-mono text-center">{p.prec_spread}</td>
                        <td className="px-2 py-1.5 font-mono font-bold bg-blue-50/40">#{p.succ_draw || '-'}</td>
                        <td className="px-2 py-1.5 font-mono bg-blue-50/40">{(p.succ_balls || []).join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Block 7 — Methodology */}
            <section className="bg-slate-800 text-white rounded-lg p-5" data-testid="block-methodology">
              <h2 className="font-bold mb-3">Block 7 — Methodology</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <p className="font-bold text-amber-300 mb-1">Prior</p>
                  <p className="text-white/80">{data.methodology?.prior}</p>
                </div>
                <div>
                  <p className="font-bold text-amber-300 mb-1">Likelihood</p>
                  <p className="text-white/80">{data.methodology?.likelihood}</p>
                </div>
                <div>
                  <p className="font-bold text-amber-300 mb-1">Posterior</p>
                  <p className="text-white/80">{data.methodology?.posterior}</p>
                </div>
                <div>
                  <p className="font-bold text-amber-300 mb-1">Bayes Factor</p>
                  <p className="text-white/80">{data.methodology?.bayes_factor}</p>
                </div>
              </div>
              <p className="text-[11px] text-white/60 mt-3 italic">
                Note: Posterior uses Laplace smoothing (α=0.5) to avoid zero-probability for unseen numbers. BF = 1 means the context gives no additional information beyond the prior.
              </p>
            </section>
          </>
        ) : null}
      </main>
    </div>
  );
}
